/* eslint-disable unicorn/filename-case */
import React, { useRef, useState } from 'react'
import { Platform, StatusBar, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useIsFocused } from '@react-navigation/native'
import { isVatom } from '@vatom/BVatom/plugin'
import { Face, FaceHandler, useBusiness } from '@vatom/sdk/react'
import { VatomModelType } from '@vatom/vatomNew/plugin'
import { Loader, PressableOpacity, Text, theme } from '@vatom/wombo'
import { observer } from 'mobx-react-lite'
import { Box } from 'native-base'

import { SpaceViewIcon } from '../../components/Icons'
import { TermsAndConditions } from '../../components/TermsAndConditions'
import { useBusinessThemeConfig } from '../../hooks/useBusinessTheme'
import { useIsDesktop } from '../../hooks/useIsDesktop'
import { useScrollLock } from '../../hooks/useScrollLock'
import logger from '../../logger'
import { AppRoutes, AppStackScreenProps } from '../../navigators'
import ActionSheetCamera from '../ActionSheets/Camera/ActionSheetCamera'
import { ARGame } from '../ARGame'
import { Crate } from '../Crate'

import ImageDisplay from './Faces/ImageDisplay'
import { WebDisplay } from './Faces/WebDisplay'
import { CardViewV2 } from './partials/CardView.V2'
import { NftDetailHeader } from './partials/NftDetailHeader'
import { isVatomNew, useToken } from './useNftDetail/useToken'
import { useNftDetail } from './useNftDetail'

export type NFTDetailProps = AppStackScreenProps<
  typeof AppRoutes.NFTDetail | typeof AppRoutes.NFTDetail_Business
>

const DisplayNFT = observer((props: NFTDetailProps) => {
  const { route } = props
  const businessIdOrName = route.params.business
  const tokenId = route.params.tokenId
  const { token, isLoading } = useToken({ tokenId })

  if (!token && isLoading) {
    // Loading
    return <Loader />
  }
  if (isVatomNew(token)) {
    return <VatomNFT businessIdOrName={businessIdOrName} token={token} {...props} />
  }

  return <NFTDetail {...props} />
})

const VatomDisplayModes: { [key: string]: React.FC<{ token: VatomModelType }> } = {
  'card-v1': WebDisplay,
  'background-v1': ImageDisplay
  // 'dynamic-image-v1': ImageDisplay,
}

const VatomNFTInner: React.FC<{ token: VatomModelType }> = observer(({ token }) => {
  // Filter viewPlacements to remove icon-v1
  const viewPlacements = token.viewPlacements.filter(p => p.placeholder.id !== 'icon-v1')

  // Error handling if no valid viewPlacements are found
  if (viewPlacements.length === 0) {
    console.error('there are no valid viewPlacements for this vatom.')
    return <Loader />
  }

  const displayMode = viewPlacements[0].placeholder.id
  const Display = VatomDisplayModes[displayMode]

  // Error handling if no Display is found
  if (!Display) {
    console.error(`there is no display for: ${displayMode}`)
    return <Loader />
  }

  return <Display token={token} />
})

export type VatomNFTProps = AppStackScreenProps<
  typeof AppRoutes.NFTDetail | typeof AppRoutes.NFTDetail_Business
> & {
  businessIdOrName?: string
  token?: VatomModelType
}

const VatomNFT = observer(({ businessIdOrName, token, route, navigation }: VatomNFTProps) => {
  const insets = useSafeAreaInsets()
  const actionSheetContainerRef = useRef<any>()
  const faceRef = useRef<FaceHandler>()

  // const faceRef = useRef<FaceHandler>()
  // const isDesktop = useIsDesktop()

  const {
    onClose,
    // isThreeD,
    // handleMessage,
    // navigateToAR,
    // onCardViewCollapsed,
    // onCardViewExpanded,
    // cardViewExpanded,
    isCameraOpen,
    setIsCameraOpen,
    qrPromise
  } = useNftDetail({
    tokenId: route.params.tokenId,
    businessIdOrName,
    navigation,
    actionSheetContainerRef,
    faceRef,
    route
  })

  useScrollLock()
  const { data: business } = useBusiness({
    business: businessIdOrName,
    businessId: businessIdOrName
  })

  const { pageTheme, brandTheme, isDark } = useBusinessThemeConfig({
    pageConfig: business?.pageConfig,
    brandConfig: business?.brandConfig
  })

  const styles = {
    background: brandTheme?.background ?? pageTheme.background,
    primary: brandTheme?.primary ?? pageTheme.active,
    primaryText: brandTheme?.primaryText ?? pageTheme.text,
    textColor: brandTheme?.text ?? pageTheme.text
  }

  const onCloseHandler = () => {
    onClose()
  }

  return (
    <View
      style={{
        flex: 1,
        flexShrink: 0,
        backgroundColor: styles.background,
        paddingTop: insets.top,
        paddingBottom: insets.bottom,
        overflow: 'hidden'
      }}
    >
      <ActionSheetCamera
        processLink={link => {
          qrPromise.current({ data: link })
          setIsCameraOpen(false)
        }}
        isOpen={isCameraOpen}
        setOpen={setIsCameraOpen}
      />
      <StatusBar
        barStyle={isDark ? 'light-content' : 'dark-content'}
        backgroundColor={styles.background}
      />
      <NftDetailHeader
        ref={actionSheetContainerRef}
        token={token as VatomModelType}
        onClose={onCloseHandler}
        theme={pageTheme}
      />

      {/* <TermsAndConditions businessId={token.studioInfo?.businessId} /> */}
      {/* {renderContent()} */}
      {token && <VatomNFTInner token={token} />}
    </View>
  )
})

const NFTDetail = observer(({ route, navigation }: NFTDetailProps) => {
  const insets = useSafeAreaInsets()
  const actionSheetContainerRef = useRef<any>()
  const faceRef = useRef<FaceHandler>()
  const isDesktop = useIsDesktop()
  useScrollLock()
  const businessIdOrName = route.params.business
  const focused = useIsFocused()

  const { data: business } = useBusiness({
    business: businessIdOrName,
    businessId: businessIdOrName
  })

  const { pageTheme, brandTheme, isDark } = useBusinessThemeConfig({
    pageConfig: business?.pageConfig,
    brandConfig: business?.brandConfig
  })

  const styles = {
    background: brandTheme?.background ?? pageTheme.background,
    primary: brandTheme?.primary ?? pageTheme.active,
    primaryText: brandTheme?.primaryText ?? pageTheme.text,
    textColor: brandTheme?.text ?? pageTheme.text
  }

  const themePage = pageTheme
  const [forceIsHidden, setForceIsHidden] = useState(false)

  const {
    token,
    onClose,
    isThreeD,
    handleMessage,
    navigateToAR,
    onCardViewCollapsed,
    onCardViewExpanded,
    cardViewExpanded,
    isCameraOpen,
    setIsCameraOpen,
    qrPromise
  } = useNftDetail({
    tokenId: route.params.tokenId,
    businessIdOrName,
    navigation,
    actionSheetContainerRef,
    faceRef,
    route
  })

  const isHidden = Platform.OS === 'android' && (cardViewExpanded || forceIsHidden || !focused)
  if (!token) {
    logger.warn('[NFTDetail.render] no token.')
    return null
  }

  const onCloseHandler = () => {
    setForceIsHidden(true)
    onClose()
  }

  const renderFaces = () => (
    <>
      <Box flexDirection={'column'} flex={1}>
        <Box
          style={{
            flex: isThreeD ? 5 / 6 : 1
          }}
        >
          {token ? (
            <Face
              ref={token.hasCardView ? undefined : faceRef}
              token={token}
              viewMode="engaged"
              onMessage={handleMessage}
              hide={isHidden}
              style={{
                backgroundColor: styles.background
              }}
            />
          ) : null}
        </Box>

        {isThreeD && !isDesktop ? (
          <Box alignItems="center" marginY={4}>
            <PressableOpacity
              onPress={navigateToAR}
              borderWidth={1}
              borderRadius={3}
              maxWidth={'100%'}
              width={228}
              height={12}
              alignItems={'center'}
              justifyContent={'center'}
              _light={{
                backgroundColor: theme.colors.grayCool[50],
                borderColor: theme.colors.grayCool[200]
              }}
              _dark={{
                backgroundColor: theme.colors.grayDarkMode[900],
                borderColor: theme.colors.grayDarkMode[800]
              }}
              style={{
                ...Platform.select({
                  ios: {
                    shadowColor: styles.primaryText ?? '#000',
                    shadowOffset: { width: 0, height: 3 },
                    shadowOpacity: 0.15,
                    shadowRadius: 4
                  },
                  android: {
                    elevation: 3
                  },
                  web: {
                    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 14px'
                  }
                })
              }}
            >
              <Box flexDirection={'row'} alignItems={'center'} justifyContent={'center'}>
                <SpaceViewIcon
                  size={6}
                  _light={{
                    color: theme.colors.textLightMode[600]
                  }}
                  _dark={{
                    color: theme.colors.textDarkMode[300]
                  }}
                />
                <Text
                  fontSize={15}
                  lineHeight={20}
                  marginLeft={2}
                  _light={{
                    color: theme.colors.textLightMode[600]
                  }}
                  _dark={{
                    color: theme.colors.textDarkMode[300]
                  }}
                  tx="nftDetail.viewSpace"
                />
              </Box>
            </PressableOpacity>
          </Box>
        ) : null}
      </Box>
      {token.hasCardView ? (
        <CardViewV2
          themePage={themePage}
          onCollapsed={onCardViewCollapsed}
          onExpanded={onCardViewExpanded}
          CardFace={<Face ref={faceRef} token={token} viewMode="card" onMessage={handleMessage} />}
          token={token}
        />
      ) : null}
    </>
  )

  const renderContent = () => {
    if (!isVatom(token)) {
      return renderFaces()
    }

    const isARGame = isVatom(token) && token.isARGame && Platform.OS === 'web'
    if (isARGame) {
      return <ARGame route={route} navigation={navigation} onClose={onClose} />
    }

    if (token.isCrate) {
      return (
        <Crate onMessage={handleMessage} route={route} navigation={navigation} onClose={onClose} />
      )
    }

    return renderFaces()
  }

  return (
    <View
      style={{
        flex: 1,
        flexShrink: 0,
        backgroundColor: styles.background,
        paddingTop: insets.top,
        paddingBottom: insets.bottom,
        overflow: 'hidden'
      }}
    >
      <ActionSheetCamera
        processLink={link => {
          qrPromise.current({ data: link })
          setIsCameraOpen(false)
        }}
        isOpen={isCameraOpen}
        setOpen={setIsCameraOpen}
      />
      <StatusBar
        barStyle={isDark ? 'light-content' : 'dark-content'}
        backgroundColor={styles.background}
      />
      <NftDetailHeader
        ref={actionSheetContainerRef}
        token={token}
        onClose={onCloseHandler}
        theme={themePage}
        contractAddress={route.params.contractAddress}
        owner={route.params.owner}
      />
      <TermsAndConditions businessId={token.studioInfo?.businessId} />
      {renderContent()}
    </View>
  )
})

export default DisplayNFT
